import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDashboards,
  useUpdateDashboardById,
  useDeleteDashboard,
  type DashboardSummary,
} from '../api/dashboards.js';
import {
  useCreateFolder,
  useDeleteFolder,
  useFolders,
  useMoveFolder,
  useRenameFolder,
  type Folder,
} from '../api/folders.js';
import { useMoveItem } from '../api/reorder.js';
import { DashboardDetail, type DashboardDetailTab } from './dashboards/DashboardDetail.js';
import { EmbedInsightsView } from './EmbedInsightsView.js';
import { NewDashboardDialog } from './dashboards/NewDashboardDialog.js';
import { DashboardFolderTree } from './dashboards/DashboardFolderTree.js';
import { MoveToFolderDialog } from './reports/MoveToFolderDialog.js';
import { PromptDialog } from '../components/dialogs/PromptDialog.js';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog.js';
import { useListSidebar, useListSidebarWidth } from '../shell/useAppChrome.js';
import { DrillBreadcrumb } from '../shell/drill-through.js';
import { isEmbedMode } from '../shell/embed.js';
import { useIsInternalAdmin } from '../api/me.js';

/**
 * Insights — live interactive dashboards and NOC wall displays.
 *
 * Layout mirrors /reports and /templates: folder tree on the left,
 * dashboard detail with tabs on the right. Folders are typed
 * 'dashboards' on the backend; the same folders API serves all three
 * surfaces.
 */
export function InsightsPage(): JSX.Element {
  // Embed mode delegates the entire layout to EmbedInsightsView — a
  // viewer-only shell with Dashboards / Reports tabs, no authoring
  // affordances. Done before any standalone-Sage state is allocated
  // so the embed path is genuinely a clean implementation rather
  // than a forked one.
  if (isEmbedMode()) return <EmbedInsightsView />;

  const dashboardsQ = useDashboards();
  const foldersQ = useFolders('dashboards');
  // CSAGE-583: only an internal admin renames, moves or deletes a standard board.
  const isInternalAdmin = useIsInternalAdmin();
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(urlId ?? null);
  // Kept here: DashboardDetail remounts per dashboard (CSAGE-496).
  const [detailTab, setDetailTab] = useState<DashboardDetailTab>('design');
  const [showNew, setShowNew] = useState(false);
  const [newDashboardFolderId, setNewDashboardFolderId] = useState<string | null>(null);
  const listSidebar = useListSidebar('insights');
  const listSidebarWidth = useListSidebarWidth('insights');

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggleFolder(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const [moveTarget, setMoveTarget] = useState<MoveTargetState | null>(null);
  const [promptDialog, setPromptDialog] = useState<PromptState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState | null>(null);

  useEffect(() => {
    if (urlId && urlId !== selected) setSelected(urlId);
  }, [urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectDashboard(id: string | null): void {
    setSelected(id);
    navigate(id ? `/insights/${id}${window.location.search}` : '/insights');
  }

  const renameFolderM = useRenameFolder();
  const moveFolderM = useMoveFolder();
  const deleteFolderM = useDeleteFolder();
  const createFolderM = useCreateFolder();
  const updateDashboardM = useUpdateDashboardById();
  const deleteDashboardM = useDeleteDashboard();
  const moveItem = useMoveItem();

  // ── Folder actions ──────────────────────────────────────────────
  function handleRenameFolder(f: Folder): void {
    setPromptDialog({
      title: 'Rename folder',
      label: 'Folder name',
      initialValue: f.name,
      submitLabel: 'Rename',
      onSubmit: async (newName) => {
        await renameFolderM.mutateAsync({ id: f.id, name: newName });
        setPromptDialog(null);
      },
    });
  }
  function handleDeleteFolder(f: Folder): void {
    setConfirmDialog({
      title: 'Delete folder',
      message: (
        <>
          Delete <strong>{f.name}</strong>? Dashboards inside move to top level; nested folders are
          deleted. This action cannot be undone.
        </>
      ),
      confirmLabel: 'Delete folder',
      onConfirm: async () => {
        await deleteFolderM.mutateAsync(f.id);
        setConfirmDialog(null);
      },
    });
  }
  function handleNewFolder(parent: Folder | null): void {
    setPromptDialog({
      title: parent ? `New folder inside "${parent.name}"` : 'New top-level folder',
      label: 'Folder name',
      placeholder: 'e.g. Operations',
      submitLabel: 'Create',
      onSubmit: async (name) => {
        await createFolderM.mutateAsync({ name, type: 'dashboards', parentId: parent?.id ?? null });
        setPromptDialog(null);
      },
    });
  }
  function handleMoveFolder(f: Folder): void {
    setMoveTarget({ kind: 'folder', folder: f });
  }

  // ── Dashboard actions ───────────────────────────────────────────
  function handleNewDashboard(parent: Folder | null): void {
    setNewDashboardFolderId(parent?.id ?? null);
    setShowNew(true);
  }
  function handleRenameDashboard(d: DashboardSummary): void {
    setPromptDialog({
      title: 'Rename dashboard',
      label: 'Dashboard name',
      initialValue: d.name,
      submitLabel: 'Rename',
      onSubmit: async (name) => {
        await updateDashboardM.mutateAsync({ id: d.id, patch: { name } });
        setPromptDialog(null);
      },
    });
  }
  function handleMoveDashboard(d: DashboardSummary): void {
    setMoveTarget({ kind: 'dashboard', dashboard: d });
  }
  function handleReorderDashboard(d: DashboardSummary, direction: 'up' | 'down'): void {
    const all = dashboardsQ.data ?? [];
    const siblings = all
      .filter((x) => (x.folderId ?? null) === (d.folderId ?? null))
      .map((x) => ({ id: x.id }));
    moveItem('dashboards', d.id, direction, siblings);
  }
  function handleReorderFolder(f: Folder, direction: 'up' | 'down'): void {
    const all = foldersQ.data ?? [];
    const siblings = all
      .filter((x) => (x.parentId ?? null) === (f.parentId ?? null) && x.type === f.type)
      .map((x) => ({ id: x.id }));
    moveItem('folders', f.id, direction, siblings);
  }
  function handleDeleteDashboard(d: DashboardSummary): void {
    setConfirmDialog({
      title: 'Delete dashboard',
      message: (
        <>
          Delete <strong>{d.name}</strong>? All versions, drafts, and kiosk tokens are dropped. Any
          embedded link or kiosk URL stops working immediately. This action cannot be undone.
        </>
      ),
      confirmLabel: 'Delete dashboard',
      onConfirm: async () => {
        await deleteDashboardM.mutateAsync(d.id);
        if (selected === d.id) selectDashboard(null);
        setConfirmDialog(null);
      },
    });
  }

  async function handleMoveTargetChosen(newParentId: string | null): Promise<void> {
    if (!moveTarget) return;
    if (moveTarget.kind === 'folder') {
      await moveFolderM.mutateAsync({ id: moveTarget.folder.id, parentId: newParentId });
    } else {
      await updateDashboardM.mutateAsync({
        id: moveTarget.dashboard.id,
        patch: { folderId: newParentId },
      });
    }
    setMoveTarget(null);
  }

  const foldersById = useMemo(() => {
    const map = new Map<string, Folder>();
    for (const f of foldersQ.data ?? []) map.set(f.id, f);
    return map;
  }, [foldersQ.data]);

  const total = dashboardsQ.data?.length ?? 0;

  return (
    <>
      <DrillBreadcrumb />

      <div
        className="split-layout"
        data-sidebar={listSidebar.mode}
        style={
          listSidebar.mode === 'expanded'
            ? ({ '--list-sidebar-width': `${listSidebarWidth.width}px` } as CSSProperties)
            : undefined
        }
      >
        <aside className="split-sidebar">
          <div className="split-sidebar-header">
            <span>Insights · {total}</span>
            <button
              type="button"
              className="btn-icon"
              onClick={listSidebar.toggle}
              title="Collapse list"
              aria-label="Collapse list"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 4L6 8l4 4" />
              </svg>
            </button>
          </div>

          <div className="split-sidebar-toolbar">
            <div className="split-sidebar-search">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M13.5 13.5l-3-3" />
              </svg>
              <input
                type="search"
                placeholder="Search folders + dashboards…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              {search && (
                <button
                  type="button"
                  className="btn-icon split-sidebar-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => handleNewFolder(null)}
              title="Create a top-level folder"
              style={{
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M1.5 5a1.5 1.5 0 0 1 1.5-1.5h3.1a1.5 1.5 0 0 1 1.05.43L8.41 5H13a1.5 1.5 0 0 1 1.5 1.5v5.5a1.5 1.5 0 0 1-1.5 1.5H3a1.5 1.5 0 0 1-1.5-1.5V5z" />
                <path d="M8 7v3.5M6.25 8.75h3.5" />
              </svg>
              New folder
            </button>
          </div>

          {dashboardsQ.isLoading && (
            <div className="text-dim text-sm" style={{ padding: 'var(--space-3)' }}>
              Loading…
            </div>
          )}
          {dashboardsQ.error && (
            <div className="alert alert--danger" style={{ margin: 'var(--space-3)' }}>
              Failed to load dashboards.
            </div>
          )}
          {dashboardsQ.data && total === 0 && !dashboardsQ.isLoading && (
            <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
              <div className="empty-state-icon">▦</div>
              <h3>No dashboards yet</h3>
              <p>Create a live dashboard for your team — or a kiosk view for the NOC wall.</p>
              <button
                className="btn btn--primary btn--sm"
                type="button"
                onClick={() => {
                  setNewDashboardFolderId(null);
                  setShowNew(true);
                }}
              >
                New dashboard
              </button>
            </div>
          )}

          <DashboardFolderTree
            folders={foldersQ.data ?? []}
            dashboards={dashboardsQ.data ?? []}
            selectedDashboardId={selected}
            onSelectDashboard={selectDashboard}
            expanded={expanded}
            onToggle={toggleFolder}
            searchQuery={search}
            canOrganizeStandard={isInternalAdmin}
            onRenameFolder={handleRenameFolder}
            onMoveFolder={handleMoveFolder}
            onDeleteFolder={handleDeleteFolder}
            onNewFolder={handleNewFolder}
            onNewDashboard={handleNewDashboard}
            onReorderFolder={handleReorderFolder}
            onRenameDashboard={handleRenameDashboard}
            onMoveDashboard={handleMoveDashboard}
            onDeleteDashboard={handleDeleteDashboard}
            onReorderDashboard={handleReorderDashboard}
          />
        </aside>

        {listSidebar.mode === 'expanded' && (
          <div
            className="split-sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize list panel"
            onDoubleClick={listSidebarWidth.reset}
            {...listSidebarWidth.bindResizer()}
            title="Drag to resize · double-click to reset"
          />
        )}

        <section>
          {listSidebar.mode === 'collapsed' && (
            <button
              type="button"
              className="split-sidebar-reopen"
              onClick={listSidebar.toggle}
              title="Open dashboard list"
              aria-label="Open dashboard list"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4l4 4-4 4" />
              </svg>
              Insights ({total})
            </button>
          )}
          {selected ? (
            <DashboardDetail
              key={selected}
              dashboardId={selected}
              tab={detailTab}
              onTabChange={setDetailTab}
            />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">▦</div>
              <h3>Select a dashboard</h3>
              <p>
                Pick a dashboard on the left, or create a new one. Dashboards are live HTML or
                widget surfaces bound to one or more datasets and refreshed on a cadence you pick.
                Mint a kiosk token to project one onto a NOC wall or a customer screen — no login,
                just a URL.
              </p>
            </div>
          )}
        </section>
      </div>

      {moveTarget && (
        <MoveToFolderDialog
          sourceLabel={
            moveTarget.kind === 'folder'
              ? `folder "${moveTarget.folder.name}"`
              : `dashboard "${moveTarget.dashboard.name}"`
          }
          folders={foldersQ.data ?? []}
          foldersById={foldersById}
          disallowId={moveTarget.kind === 'folder' ? moveTarget.folder.id : undefined}
          onCancel={() => setMoveTarget(null)}
          onChoose={handleMoveTargetChosen}
        />
      )}
      {promptDialog && <PromptDialog {...promptDialog} onCancel={() => setPromptDialog(null)} />}
      {confirmDialog && (
        <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />
      )}

      {showNew && (
        <NewDashboardDialog
          initialFolderId={newDashboardFolderId}
          onClose={() => {
            setShowNew(false);
            setNewDashboardFolderId(null);
          }}
          onCreated={(d: DashboardSummary) => {
            setShowNew(false);
            setNewDashboardFolderId(null);
            selectDashboard(d.id);
          }}
        />
      )}
    </>
  );
}

type MoveTargetState =
  | { kind: 'folder'; folder: Folder }
  | { kind: 'dashboard'; dashboard: DashboardSummary };

type PromptState = {
  title: string;
  description?: string | undefined;
  label: string;
  initialValue?: string | undefined;
  placeholder?: string | undefined;
  submitLabel?: string | undefined;
  destructive?: boolean | undefined;
  onSubmit: (value: string) => Promise<void>;
};
type ConfirmState = {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  destructive?: boolean | undefined;
  requireTyping?: string | undefined;
  onConfirm: () => Promise<void>;
};
